// Maps the text a user selected in the rendered article back to a span of the
// section's markdown source. The rendered text has no markdown syntax and its
// whitespace differs from the source, so the match runs on a stripped,
// whitespace-normalised copy of the source that remembers every character's
// original offset.

export interface Span {
  start: number;
  end: number;
}

const LINE_PREFIX = /^(\s*)(?:[-*+]\s+|\d+[.)]\s+|>\s?|#{1,6}\s+)/;
const MARKER_CHARS = new Set(['*', '_', '`']);

/** The source with markdown syntax removed and whitespace collapsed, plus an offset map. */
function stripped(body: string): { text: string; map: number[] } {
  const out: string[] = [];
  const map: number[] = [];
  let lastWasSpace = true; // leading whitespace is dropped
  const emit = (ch: string, at: number) => {
    const isSpace = /\s/.test(ch) || ch === '|';
    if (isSpace) {
      if (lastWasSpace) return;
      out.push(' ');
      map.push(at);
      lastWasSpace = true;
      return;
    }
    out.push(ch);
    map.push(at);
    lastWasSpace = false;
  };

  let pos = 0;
  for (const line of body.split('\n')) {
    const lineStart = pos;
    pos += line.length + 1;
    const prefix = line.match(LINE_PREFIX);
    let i = prefix ? prefix[0].length : 0;
    // A blank or prefix-only line separates words.
    emit(' ', lineStart + Math.max(0, i - 1));
    while (i < line.length) {
      const ch = line[i];
      if (ch === '[') {
        const close = line.indexOf('](', i);
        const paren = close === -1 ? -1 : line.indexOf(')', close + 2);
        if (close !== -1 && paren !== -1) {
          for (let k = i + 1; k < close; k++) emit(line[k], lineStart + k);
          i = paren + 1;
          continue;
        }
      }
      if (ch === '!' && line[i + 1] === '[') {
        i++;
        continue;
      }
      if (MARKER_CHARS.has(ch)) {
        i++;
        continue;
      }
      emit(ch, lineStart + i);
      i++;
    }
  }
  // Drop a trailing space.
  while (out.length && out[out.length - 1] === ' ') {
    out.pop();
    map.pop();
  }
  return { text: out.join(''), map };
}

const normalize = (s: string) => s.replace(/[\s|]+/g, ' ').trim();

/** Grow the span over the formatting that wraps it: `**bold**`, `[link](url)`. */
function extendOverMarkers(body: string, span: Span): Span {
  let { start, end } = span;
  while (start > 0 && MARKER_CHARS.has(body[start - 1])) start--;
  while (end < body.length && MARKER_CHARS.has(body[end])) end++;
  if (body[start - 1] === '[') {
    // The span starts inside a link's text: take the whole `[text](url)` when
    // the link closes at or inside the span.
    const linkClose = body.indexOf('](', start);
    if (linkClose !== -1 && linkClose <= end) {
      const paren = body.indexOf(')', linkClose + 2);
      if (paren !== -1) {
        start--;
        if (paren + 1 > end) end = paren + 1;
      }
    }
  }
  return { start, end };
}

export function locateSpan(body: string, selectedText: string): Span | null {
  const wanted = normalize(selectedText);
  if (!wanted) return null;

  const exact = body.indexOf(selectedText.trim());
  if (exact !== -1 && selectedText.trim() !== '') {
    return extendOverMarkers(body, { start: exact, end: exact + selectedText.trim().length });
  }

  const { text, map } = stripped(body);
  const idx = text.indexOf(wanted);
  if (idx === -1) return null;
  const start = map[idx];
  const end = map[idx + wanted.length - 1] + 1;
  return extendOverMarkers(body, { start, end });
}
